import React from 'react';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type KpiCardVariant = 'default' | 'emerald' | 'indigo' | 'rose' | 'amber';

export interface KpiCardProps {
  title: string;
  value: React.ReactNode | string | number;
  subtitle?: string;
  icon?: LucideIcon | React.ElementType;
  badge?: React.ReactNode | string;
  variant?: KpiCardVariant;
  loading?: boolean;
  className?: string;
}

const variantStyles: Record<
  KpiCardVariant,
  { iconBg: string; iconColor: string; valueColor: string }
> = {
  default: {
    iconBg: 'bg-slate-100',
    iconColor: 'text-slate-700',
    valueColor: 'text-slate-900',
  },
  emerald: {
    iconBg: 'bg-emerald-50 border border-emerald-100',
    iconColor: 'text-emerald-700',
    valueColor: 'text-emerald-900',
  },
  indigo: {
    iconBg: 'bg-indigo-50 border border-indigo-100',
    iconColor: 'text-indigo-700',
    valueColor: 'text-indigo-900',
  },
  rose: {
    iconBg: 'bg-rose-50 border border-rose-100',
    iconColor: 'text-rose-700',
    valueColor: 'text-rose-900',
  },
  amber: {
    iconBg: 'bg-amber-50 border border-amber-100',
    iconColor: 'text-amber-700',
    valueColor: 'text-amber-900',
  },
};

export const KpiCard: React.FC<KpiCardProps> = ({
  title,
  value,
  subtitle,
  icon: Icon,
  badge,
  variant = 'default',
  loading = false,
  className,
}) => {
  const styles = variantStyles[variant] || variantStyles.default;

  return (
    <div
      className={cn(
        'bg-white border border-slate-200/80 shadow-xs rounded-xl p-3.5 sm:p-4 flex flex-col justify-between transition-shadow hover:shadow-sm',
        className
      )}
    >
      {/* Top Header Row */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider truncate">
          {title}
        </span>
        {Icon && (
          <div className={cn('p-1.5 rounded-lg flex-shrink-0', styles.iconBg, styles.iconColor)}>
            <Icon className="w-4 h-4" />
          </div>
        )}
      </div>

      {/* Value Row */}
      <div className="min-w-0">
        {loading ? (
          <div className="h-7 w-28 bg-slate-200 animate-pulse rounded my-0.5" />
        ) : (
          <div
            className={cn(
              'text-xl sm:text-2xl font-bold font-mono tracking-tight truncate',
              styles.valueColor
            )}
          >
            {value}
          </div>
        )}
      </div>

      {/* Subtitle / Badge Footer */}
      {(subtitle || badge) && (
        <div className="flex items-center justify-between gap-2 mt-1 min-w-0">
          {subtitle && (
            <p className="text-[11px] text-slate-400 truncate leading-normal">{subtitle}</p>
          )}
          {badge && (
            <div className="flex-shrink-0">
              {typeof badge === 'string' ? (
                <span className="px-1.5 py-0.2 text-[10px] font-semibold bg-slate-100 text-slate-700 rounded">
                  {badge}
                </span>
              ) : (
                badge
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
